import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { FreudMessageService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ITask } from 'app/interfaces/task.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

import { UnifiedPromptuaryService } from './../promptuary-form/unified-promptuary.service';

@Component({
	selector: 'promptuary-task',
	templateUrl: './promptuary-task.component.html',
	styleUrls: ['./promptuary-task.component.scss'],
})
export class PromptuaryTaskComponent implements OnInit, OnDestroy {
	constructor(
		public fb: FormBuilder,
		public unifiedPromptuaryService: UnifiedPromptuaryService,
		public promptuaryService: PromptuaryService,
		public messageService: FreudMessageService,
		public translateService: TranslateService,
		public atService: ActionTrackerService
	) {}

	@Input()
	appointmentId;

	@Input()
	contactId;

	@Input()
	professionalId;

	@Input()
	formGroup: FormGroup;

	@Input() name: string;

	newLink = '';
	isHelperModalOpen = false;
	isInvalidUrl = false;
	autosaveSubscription;
	public autoSaveSubject: Subject<any> = new Subject();
	public linkLoading = false;
	public showAlert = false;
	public showLinks = false;

	get description() {
		return this.formGroup.get('task').get('description');
	}

	get links() {
		return this.formGroup.get('task').get('links') as FormArray;
	}

	get taskId() {
		return this.formGroup.get('task').get('id');
	}

	ngOnInit(): void {
		this.createDebounceAutoSave();
		this.getTaskInitialValue();
		this.trackTaskView();

		this.formGroup.valueChanges.subscribe(() => {
			this.updateAlerts();
		});
	}

	ngOnDestroy(): void {
		this.autosaveSubscription.unsubscribe();
	}

	getTaskInitialValue() {
		this.promptuaryService
			.getProfessionalTask(this.appointmentId, this.contactId)
			.subscribe(
				(task: ITask) => {
					if (task) {
						this.taskId.setValue(task.id);
						this.description.setValue(task.description);
						task.attachments?.forEach((link) => {
							this.links.push(this.fb.control(link));
						});
						this.description.updateValueAndValidity();
					}
				},
				() => {
					this.description.setValue('');
				}
			);
	}

	createDebounceAutoSave() {
		this.autosaveSubscription = this.autoSaveSubject
			.pipe(
				tap(() => {
					this.unifiedPromptuaryService.setAutoSaveLoading('task');
					this.setBreadcrumb('starting autosave', {
						property: 'promptuary-task',
					});
				}),
				debounceTime(1200),
				switchMap((task) => {
					return this.promptuaryService.saveProfessionalTask(task);
				})
			)
			.subscribe(
				() => {
					this.setBreadcrumb('autosave complete', {
						property: 'promptuary-task',
					});
					this.unifiedPromptuaryService.autosaveComplete('task');
					this.description.updateValueAndValidity();
				},
				() => {
					throw Error(`Auto save error in property: promptuary-task.`);
				}
			);
	}

	openHelperModal() {
		this.isHelperModalOpen = true;
	}

	saveTask() {
		if (!this.description.value && this.links.value.length === 0) {
			return;
		}

		const task: ITask = {
			eventId: this.appointmentId,
			professionalId: this.professionalId,
			customerId: this.contactId,
			description: this.description.value,
		};

		this.autoSaveSubject.next(task);
	}

	removeLink(linkId) {
		this.linkLoading = true;
		this.promptuaryService.deleteTaskLink(linkId).subscribe(
			() => {
				const index = this.links.value.findIndex((link) => link.id === linkId);
				this.links.removeAt(index);
			},
			() => {
				const message = this.translateService.instant(
					'PROMPTUARY.LINK_REMOVE_ERROR'
				);
				this.messageService.add({
					summary: message,
					severity: 'error',
				});
			},
			() => {
				this.linkLoading = false;
				this.description.updateValueAndValidity();
			}
		);
	}

	addLink() {
		if (this.isValidHttpUrl(this.newLink) && !this.linkAlreadExists()) {
			this.linkLoading = true;
			this.promptuaryService
				.createTaskLink(this.newLink, this.taskId.value)
				.subscribe(
					(res: any) => {
						res.forEach((link) => {
							this.links.push(this.fb.control(link));
						});
						this.newLink = '';
						this.isInvalidUrl = false;
					},
					() => {
						const message = this.translateService.instant(
							'PROMPTUARY.LINK_ADD_ERROR'
						);
						this.messageService.add({
							summary: message,
							severity: 'error',
						});
					},
					() => {
						this.linkLoading = false;
						this.description.updateValueAndValidity();
					}
				);
		} else {
			this.isInvalidUrl = true;
		}
	}

	linkAlreadExists() {
		return this.links.value.some(
			(link) => link.specifications.url === this.newLink
		);
	}

	isValidHttpUrl(string) {
		let url;

		try {
			url = new URL(string);
		} catch (_) {
			return false;
		}

		return url.protocol === 'http:' || url.protocol === 'https:';
	}

	setBreadcrumb(message, property?) {
		Sentry.addBreadcrumb({
			category: 'ui.autosave',
			message: message,
			data: property ?? {},
			level: 'info',
			type: 'default',
		});
	}

	trackTaskView() {
		this.atService.eventTracker(
			SCOPE_TYPE.Promptuary,
			SUBSCOPE_TYPE.Session,
			ITEM_TYPE.Task,
			INTERACTION_TYPE.Seen,
			{
				sessionId: this.appointmentId,
				contactId: this.contactId,
				professionalId: this.professionalId,
			}
		);
	}

	updateAlerts() {
		const hasDescription = this.formGroup.get('task').get('description').value;
		const hasLinks = this.formGroup.get('task').get('links').value.length > 0;

		if (hasDescription || hasLinks) {
			this.showAlert = true;
		} else {
			this.showAlert = false;
		}

		if (hasLinks) {
			this.showLinks = true;
		} else {
			this.showLinks = false;
		}
	}
}
